import axios, { AxiosRequestHeaders, Method } from 'axios';
import axiosRetry from 'axios-retry';
import { authService } from 'services';
import { API_ENDPOINT } from 'settings';
import store from 'store';

import { selectUser } from '../../store/selectors';
import { IAuth } from '../../types/interfaces';
import ApiError from './apiError';

const API_TIMEOUT = 30000;

class ApiService {
  private isRefreshingToken = false;

  public async request<T>(
    method: Method,
    url: string,
    data: any = null,
    retries?: number,
    ignoreBearerToken = false,
    hideToastMessage = false,
    signal?: AbortSignal
  ) {
    const client = axios.create();
    let token = undefined;

    if (retries) {
      axiosRetry(client, {
        retries,
        shouldResetTimeout: true,
        retryCondition: () => true
      });
    }

    try {
      if (!this.isRefreshingToken && !ignoreBearerToken) {
        token = await this.getBearerToken();
      }

      const headers: AxiosRequestHeaders = token ? { Authorization: token } : {};

      const response = await client.request<T>({
        url: `${API_ENDPOINT}${url}`,
        signal,
        method,
        timeout: API_TIMEOUT,
        params: method === 'GET' ? data : null,
        data: ['POST', 'PUT'].includes(method) ? data : null,
        responseType: 'json',
        headers: {
          'Content-type': 'application/json',
          ...headers
        }
      });

      return response.data;
    } catch (err) {
      throw new ApiError(err.config, err.response, err, hideToastMessage);
    }
  }

  public get<T = any>(url: string, params?: any, retries?: number, hideToastMessage = false, signal?: AbortSignal) {
    return this.request<T>('GET', url, params, retries, false, hideToastMessage, signal);
  }

  public post<T = any>(url: string, body: any, retries?: number, hideToastMessage = false) {
    return this.request<T>('POST', url, body, retries, false, hideToastMessage);
  }

  public put<T = any>(url: string, body: any, retries?: number, hideToastMessage = false) {
    return this.request<T>('PUT', url, body, retries, false, hideToastMessage);
  }

  public delete<T = any>(url: string, params?: any, retries?: number, hideToastMessage = false) {
    return this.request<T>('DELETE', url, params, retries, false, hideToastMessage);
  }

  private async getBearerToken() {
    const { token: userToken, refresh: refreshToken, exp } = selectUser(store.getState());

    if (!userToken) {
      return;
    }

    const now = (Date.now().valueOf() + 500) / 1000;
    if (!exp || exp > now) {
      return `Bearer ${userToken}`;
    }

    try {
      if (!this.isRefreshingToken) {
        this.isRefreshingToken = true;

        const { token, refresh } = await this.request<IAuth>(
          'POST',
          '/auth/refresh',
          { refresh: refreshToken },
          0,
          true
        );

        this.isRefreshingToken = false;
        authService.setToken({ token, refresh });

        return `Bearer ${token}`;
      }
    } catch (err) {
      window.location.reload();
    }
  }
}

export interface IApiService extends ApiService {}
export { ApiService };
export default new ApiService();
